/* storybook-check-ignore */
import { Box, Flex, Text } from '@opendoor/bricks/core';
import { Listing, ListingWithComputedProperties } from 'declarations/exclusives/listing';
import {
  getIsOOMLS,
  getIsSellerOfferExperiment,
  getListingPriceCents,
  getOriginalListingPriceCents,
} from 'helpers/exclusives/listing';
import { ReactNode } from 'react';
import AppraisalPriceMatchGuaranteeInfo from './AppraisalPriceMatchGuaranteeInfo';
import BackOutAnytimeInfo from './BackOutAnytimeInfo';

function BreakdownRow({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <Flex flexDirection="row" justifyContent="space-between" alignItems="center">
      <Text fontSize="s1" color="neutrals100" opacity="0.8" lineHeight="s1" flex={1}>
        {title}
      </Text>
      <Text
        fontSize="s1"
        color="neutrals100"
        lineHeight="s1"
        fontWeight="semibold"
        flex={1}
        textAlign="right"
      >
        {children}
      </Text>
    </Flex>
  );
}

const EARNEST_MONEY_PERCENTAGE = 1;

export default function PriceBreakdown({ listing }: { listing: ListingWithComputedProperties }) {
  const isSellerOfferExperiment = getIsSellerOfferExperiment(listing);
  const isOOMLS = getIsOOMLS(listing);
  if (isSellerOfferExperiment) {
    return <SellerOwnedPriceBreakdown listing={listing} />;
  }
  if (isOOMLS) {
    return <OOMLSPriceBreakdown listing={listing} />;
  }
  return null;
}

function SellerOwnedPriceBreakdown({ listing }: { listing: Listing }) {
  const listingOriginalPriceCents = getOriginalListingPriceCents(listing);
  const youWouldPayCents = getListingPriceCents(listing);
  if (!listingOriginalPriceCents || !youWouldPayCents) {
    return null;
  }
  const sellerAskingPrice = listingOriginalPriceCents / 100;
  const youWouldPay = Math.floor(youWouldPayCents / 100);
  const exclusiveSavingsValue = sellerAskingPrice - youWouldPay;
  const earnestMoney = Math.floor((youWouldPay * EARNEST_MONEY_PERCENTAGE) / 100);
  return (
    <Flex flexDirection="column" width="100%" gridGap="1rem" marginBottom="1.5rem">
      <Text fontWeight="semibold" fontSize="s2">
        Price Breakdown
      </Text>
      <Flex flexDirection="row" justifyContent="space-between">
        <Flex flexDirection="column" gridGap="0.4rem">
          <Text fontSize="s1" fontWeight="semibold">
            Your Exclusive Price
          </Text>
          <Text fontSize="s4" lineHeight="s3" color="brand50" fontWeight="bold">
            ${youWouldPay.toLocaleString()}
          </Text>
        </Flex>
        <Flex flexDirection="column" gridGap="0.4rem" alignItems="self-end">
          <Text fontSize="s1" textAlign="right">
            Seller’s Asking Price
          </Text>
          <Text fontSize="s4" lineHeight="s3" textDecoration="line-through">
            ${sellerAskingPrice.toLocaleString()}
          </Text>
        </Flex>
      </Flex>
      <Flex
        position="relative"
        bg="#FAFBFE"
        padding="1.5rem 1.875rem 1.875rem 1.875rem"
        border="1px solid rgba(8, 45, 230, 0.2)"
        borderRadius="semiRounded"
        flexDirection="column"
        gridGap="1rem"
        mt="0.75rem"
      >
        <Box
          bg="#FAFBFE"
          borderLeft="1px solid rgba(8, 45, 230, 0.2)"
          borderTop="1px solid rgba(8, 45, 230, 0.2)"
          position="absolute"
          transform="rotate(45deg)"
          height="1rem"
          width="1rem"
          top="-0.5rem"
          left="5rem"
        />
        <BreakdownRow title="Exclusive Savings">
          - ${exclusiveSavingsValue.toLocaleString()}
        </BreakdownRow>
        <Box width="100%" height="1px" bg="#CED5FA" />
        <Flex flexDirection="column" gridGap="1.5rem">
          {[
            {
              title: `Earnest money (${EARNEST_MONEY_PERCENTAGE}%)`,
              content: `$${earnestMoney.toLocaleString()}`,
            },
            {
              title: 'Balance paid at closing',
              content: `$${(youWouldPay - earnestMoney).toLocaleString()}`,
            },
            {
              title: 'Option fee',
              content: (
                <>
                  <Text as="span" textDecoration="line-through" opacity="0.6" fontWeight="normal">
                    $100
                  </Text>{' '}
                  $0
                </>
              ),
            },
            {
              title: (
                <>
                  Agent fees <br />
                  <Text as="span" opacity="0.6" fontWeight="normal" fontSize="s0" lineHeight="s000">
                    When you buy directly with Exclusives, there are no agent fees. If you use a
                    buyer agent, you're responsible for their fees.
                  </Text>
                </>
              ),
              content: '$0',
            },
            {
              title: 'Closing costs',
              content: (
                <Text as="span" fontWeight="normal">
                  Varies by sale
                </Text>
              ),
            },
          ].map(({ title, content }, i) => (
            <BreakdownRow key={`breakDownRow${i}`} title={title}>
              {content}
            </BreakdownRow>
          ))}
        </Flex>
      </Flex>
      <Flex flexDirection="row" justifyContent="space-between" gridGap="1rem" flexWrap="wrap">
        <AppraisalPriceMatchGuaranteeInfo isSellerOfferExperiment />
      </Flex>
    </Flex>
  );
}

function OOMLSPriceBreakdown({ listing }: { listing: Listing }) {
  const priceCents = getListingPriceCents(listing);
  if (!priceCents) {
    return null;
  }
  const price = priceCents / 100;
  const earnestMoney = Math.floor((price * EARNEST_MONEY_PERCENTAGE) / 100);
  return (
    <Flex flexDirection="column" width="100%" gridGap="1rem" marginBottom="1.5rem">
      <Text fontWeight="semibold" fontSize="s2">
        Price Breakdown
      </Text>
      <Flex flexDirection="row" justifyContent="space-between">
        <Flex flexDirection="column" gridGap="0.4rem">
          <Text fontSize="s1" fontWeight="semibold">
            Exclusive Price
          </Text>
          <Text fontSize="s4" lineHeight="s3" color="brand50" fontWeight="bold">
            ${price.toLocaleString()}
          </Text>
        </Flex>
      </Flex>
      <Flex
        position="relative"
        bg="#FAFBFE"
        padding="1.5rem 1.875rem 1.875rem 1.875rem"
        border="1px solid rgba(8, 45, 230, 0.2)"
        borderRadius="semiRounded"
        flexDirection="column"
        gridGap="1rem"
        mt="0.75rem"
      >
        <Box
          bg="#FAFBFE"
          borderLeft="1px solid rgba(8, 45, 230, 0.2)"
          borderTop="1px solid rgba(8, 45, 230, 0.2)"
          position="absolute"
          transform="rotate(45deg)"
          height="1rem"
          width="1rem"
          top="-0.5rem"
          left="5rem"
        />
        <Flex flexDirection="column" gridGap="1.5rem">
          {[
            {
              title: `Earnest money (${EARNEST_MONEY_PERCENTAGE}%)`,
              content: `$${earnestMoney.toLocaleString()}`,
            },
            {
              title: 'Balance paid at closing',
              content: `$${(price - earnestMoney).toLocaleString()}`,
            },
            {
              title: 'Option fee',
              content: (
                <>
                  <Text as="span" textDecoration="line-through" opacity="0.6" fontWeight="normal">
                    $100
                  </Text>{' '}
                  $0
                </>
              ),
            },
            {
              title: (
                <>
                  Agent fees <br />
                  <Text as="span" opacity="0.6" fontWeight="normal" fontSize="s0" lineHeight="s000">
                    When you buy directly with Exclusives, there are no agent fees. If you use a
                    buyer agent, you're responsible for their fees.
                  </Text>
                </>
              ),
              content: '$0',
            },
            {
              title: 'Closing costs',
              content: (
                <Text as="span" fontWeight="normal">
                  based on sale price
                </Text>
              ),
            },
          ].map(({ title, content }, i) => (
            <BreakdownRow key={`breakDownRow${i}`} title={title}>
              {content}
            </BreakdownRow>
          ))}
        </Flex>
      </Flex>
      <Flex flexDirection="row" justifyContent="space-between" gridGap="1rem" flexWrap="wrap">
        <BackOutAnytimeInfo isSellerOfferExperiment={false} />
        <AppraisalPriceMatchGuaranteeInfo isSellerOfferExperiment={false} />
      </Flex>
    </Flex>
  );
}
